/**
 * « Mes trousses » (`/trousses`, migration 0738) : la collection des 32 couvertures. La première case fait
 * tourner la roue du jour (une fois par jour, 1 chance sur 10 de gagner une trousse). Toucher une trousse
 * qu'on a : la porter, l'offrir par un lien / un QR code, ou — avec 3 exemplaires du même modèle —
 * l'échanger contre 1 exemplaire du modèle au-dessus (migration 0739). Une trousse qu'on n'a pas se
 * montre grisée, avec sa rareté.
 */
import { useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowUpRight, Gift, Lock, Sparkles } from 'lucide-react';
import { Button, Card } from '@jeraisonne/ui';
import { InviteLinkField } from '@/components/invite-link-field';
import { QrCodeImage } from '@/components/qr-code';
import { useAuth } from '@/lib/auth';
import {
  collectionCount,
  formatCopies,
  goldSlices,
  myTrousseKey,
  serialLabel,
  serialsLabel,
  spinTrousseWheel,
  tradeOption,
  trousseCollectionKey,
  trousseGiftErrorMessage,
  trousseGiftUrl,
  trousseImage,
  trousseRarity,
  trousseRarityColor,
  useCancelTrousseGift,
  useCreateTrousseGift,
  useEquipFromCollection,
  useTradeTrousses,
  useTrousseCollection,
  wheelStatusLabel,
  winChanceLabel,
  type CollectionTrousse,
} from '@/lib/trousses';
import { TrousseShowcase } from '@/features/trousse/trousse-showcase';
import { WheelDialog, WheelDisc } from './wheel';

const SOURCE_LABEL: Record<NonNullable<CollectionTrousse['source']>, string> = {
  default: 'Ta toute première trousse',
  reward: 'Gagnée à la roue du jour',
  gift: 'On te l’a offerte',
  admin: 'Offerte par JeRaisonne',
  trade: 'Obtenue en échangeant 3 trousses',
};

export function TrousseCollectionPage({ onClose }: { onClose?: () => void } = {}) {
  const { user } = useAuth();
  const router = useRouter();
  const qc = useQueryClient();
  const userId = user?.id ?? '';
  const { data, isLoading, error } = useTrousseCollection(user?.id);
  const equip = useEquipFromCollection(userId);
  const [selected, setSelected] = useState<string | null>(null);
  const [wheelOpen, setWheelOpen] = useState(false);
  // l'heure de la prochaine roue se met à jour toute seule
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (user?.is_anonymous) {
    return (
      <PageShell>
        <p className="text-ink-600 text-sm">
          Crée ton compte pour avoir ta collection de trousses.
        </p>
      </PageShell>
    );
  }

  const catalog = data?.catalog ?? [];
  const { owned, total } = collectionCount(catalog);
  const spin = data?.spin;
  const current = catalog.find((t) => t.slug === selected) ?? null;

  return (
    <PageShell
      onBack={onClose ?? (() => router.history.back())}
      backLabel={onClose ? 'Fermer' : 'Retour'}
      subtitle={data ? `${owned} / ${total} trousses` : undefined}
    >
      {error ? (
        <p role="alert" className="text-error-600 text-sm">
          Ta collection n’a pas pu être chargée. Réessaie dans un instant.
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" aria-busy={isLoading}>
          <li>
            <button
              type="button"
              data-testid="wheel-cell"
              disabled={!spin?.can_spin}
              onClick={() => setWheelOpen(true)}
              className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-[#f5b82e] bg-[#061433] p-2 text-center text-white active:scale-[0.98] disabled:opacity-80"
            >
              <span className={spin?.can_spin ? 'animate-[spin_12s_linear_infinite]' : ''}>
                <WheelDisc size={56} gold={goldSlices(spin?.chance ?? 0.1)} />
              </span>
              <span className="text-xs font-bold leading-tight">Roue du jour</span>
              <span className="text-[0.75rem] leading-tight text-[#f5d27a]">
                {!spin ? '…' : wheelStatusLabel(spin)}
              </span>
            </button>
          </li>
          {(isLoading ? placeholders() : catalog).map((t) => (
            <li key={t.slug}>
              <TrousseTile trousse={t} onOpen={() => setSelected(t.slug)} />
            </li>
          ))}
        </ul>
      )}

      {data && (
        <p className="text-ink-500 text-center text-xs">
          Un tour de roue par jour, après un exercice terminé : {winChanceLabel(data.win_chance)}{' '}
          aujourd’hui, une de plus chaque jour perdu d’affilée (retour à 1 si tu sautes un jour ou
          si tu gagnes). Plus une trousse est rare, moins elle sort. {data.trade_cost} exemplaires
          d’un même modèle s’échangent contre 1 exemplaire du modèle au-dessus.
        </p>
      )}

      {current && (
        <TrousseSheet
          trousse={current}
          catalog={catalog}
          tradeCost={data?.trade_cost ?? 3}
          userId={userId}
          equipping={equip.isPending}
          onEquip={() => equip.mutate(current.slug)}
          onTraded={(slug) => setSelected(slug)}
          onClose={() => setSelected(null)}
        />
      )}

      {wheelOpen && data && (
        <WheelDialog
          winChance={data.win_chance}
          onSpin={async () => {
            const r = await spinTrousseWheel();
            void qc.invalidateQueries({ queryKey: trousseCollectionKey(userId) });
            void qc.invalidateQueries({ queryKey: myTrousseKey(userId) });
            return r;
          }}
          onClose={() => setWheelOpen(false)}
          onEquip={(slug) => {
            equip.mutate(slug);
            setWheelOpen(false);
          }}
        />
      )}
    </PageShell>
  );
}

function placeholders(): CollectionTrousse[] {
  return Array.from({ length: 11 }, (_, i) => ({
    slug: `…${i}`,
    name: '',
    rank: i,
    copies: 1,
    taken: 0,
    image_path: '',
    is_default: false,
    owned: false,
    count: 0,
    serials: [],
    serial: null,
    source: null,
    equipped: false,
    gift_token: null,
  }));
}

function PageShell({
  children,
  onBack,
  backLabel = 'Retour',
  subtitle,
}: {
  children: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  subtitle?: string;
}) {
  return (
    <div className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:py-8">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-ink-600 -ml-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm font-semibold"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {backLabel}
        </button>
      )}
      <div>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">Mes trousses</h1>
        {subtitle && <p className="text-ink-500 mt-1 text-sm">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function TrousseTile({ trousse: t, onOpen }: { trousse: CollectionTrousse; onOpen: () => void }) {
  if (!t.image_path) {
    return <div className="bg-ink-100 aspect-[3/4] w-full animate-pulse rounded-2xl" />;
  }
  const given = !!t.gift_token;
  const label = t.owned
    ? `${t.name}${t.count > 1 ? `, ${t.count} exemplaires` : ''}${t.equipped ? ', portée' : ''}`
    : given
      ? `${t.name}, offerte`
      : `${t.name}, pas encore dans ta collection`;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      data-owned={t.owned ? '1' : '0'}
      className={`border-ink-100 relative flex aspect-[3/4] w-full flex-col overflow-hidden rounded-2xl border bg-white text-left active:scale-[0.98] ${
        t.equipped ? 'ring-2 ring-[#f5b82e] ring-offset-2' : ''
      }`}
    >
      <img
        src={trousseImage(t.image_path)}
        alt=""
        loading="lazy"
        className={`min-h-0 w-full flex-1 object-cover ${t.owned ? '' : 'opacity-40 grayscale'}`}
      />
      <span className="space-y-0.5 px-2 py-1.5">
        <span className="text-ink-900 block truncate text-xs font-bold">{t.name}</span>
        <span className="text-ink-500 block truncate text-[0.75rem]">
          {trousseRarity(t.copies)}
        </span>
      </span>
      {!t.owned && !given && (
        <Lock className="absolute right-2 top-2 h-4 w-4 text-white drop-shadow" aria-hidden />
      )}
      {t.equipped && (
        <span className="absolute left-1.5 top-1.5 rounded-full bg-[#f5b82e] px-2 py-0.5 text-[0.75rem] font-bold text-[#061433]">
          Portée
        </span>
      )}
      {given && (
        <span className="bg-accent-500 absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[0.75rem] font-bold text-white">
          Offerte
        </span>
      )}
      {t.count > 1 && (
        <span
          data-testid="copies-badge"
          className="absolute right-1.5 top-1.5 rounded-full bg-[#061433] px-2 py-0.5 text-[0.75rem] font-bold text-white"
        >
          ×{t.count}
        </span>
      )}
    </button>
  );
}

function TrousseSheet({
  trousse: t,
  catalog,
  tradeCost,
  userId,
  equipping,
  onEquip,
  onTraded,
  onClose,
}: {
  trousse: CollectionTrousse;
  catalog: CollectionTrousse[];
  tradeCost: number;
  userId: string;
  equipping: boolean;
  onEquip: () => void;
  /** l'échange a réussi : la fiche passe sur la trousse reçue */
  onTraded: (slug: string) => void;
  onClose: () => void;
}) {
  const createGift = useCreateTrousseGift(userId);
  const cancelGift = useCancelTrousseGift(userId);
  const trade = useTradeTrousses(userId);
  const [confirmGift, setConfirmGift] = useState(false);
  const [confirmTrade, setConfirmTrade] = useState(false);
  const [traded, setTraded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // une autre fiche : on repart de zéro
  useEffect(() => {
    setConfirmGift(false);
    setConfirmTrade(false);
    setError(null);
  }, [t.slug]);

  const token = t.gift_token;
  const option = tradeOption(catalog, t, tradeCost);

  async function doTrade() {
    setError(null);
    try {
      const got = await trade.mutateAsync(t.slug);
      setConfirmTrade(false);
      setTraded(got.name);
      onTraded(got.slug);
    } catch (e) {
      setError(trousseGiftErrorMessage((e as Error).message));
    }
  }

  async function gift() {
    setError(null);
    try {
      await createGift.mutateAsync(t.slug);
      setConfirmGift(false);
    } catch (e) {
      setError(trousseGiftErrorMessage((e as Error).message));
    }
  }

  async function takeBack() {
    if (!token) return;
    setError(null);
    try {
      await cancelGift.mutateAsync(token);
    } catch (e) {
      setError(trousseGiftErrorMessage((e as Error).message));
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-[rgba(6,20,51,0.6)] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trousse-sheet-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="ocean:bg-[#061433] max-h-[92dvh] w-full max-w-md space-y-4 overflow-y-auto rounded-b-none bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-b-2xl">
        {t.owned || token || traded === t.name ? (
          <div className="space-y-1 text-center">
            <TrousseShowcase key={t.slug} trousse={t} celebrate={traded === t.name} />
            <h2
              id="trousse-sheet-title"
              className="font-display text-ink-900 text-2xl font-extrabold"
            >
              {t.name}
            </h2>
            <p
              className="text-sm font-bold capitalize"
              style={{ color: trousseRarityColor(t.copies) }}
            >
              {trousseRarity(t.copies)}
            </p>
            <TrousseFacts trousse={t} token={token} />
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <img
              src={trousseImage(t.image_path)}
              alt=""
              className="h-28 w-24 shrink-0 rounded-2xl object-cover opacity-50 shadow grayscale"
            />
            <div className="min-w-0 space-y-1">
              <h2
                id="trousse-sheet-title"
                className="font-display text-ink-900 text-xl font-extrabold"
              >
                {t.name}
              </h2>
              <p className="text-ink-600 text-sm font-semibold capitalize">
                {trousseRarity(t.copies)}
              </p>
              <TrousseFacts trousse={t} token={token} />
            </div>
          </div>
        )}

        {traded && (
          <p role="status" className="text-sm font-semibold text-green-700">
            Échange fait : « {traded} » est dans ta collection.
          </p>
        )}

        {token ? (
          <div className="space-y-3">
            <p className="text-ink-800 text-sm">
              <Gift className="text-accent-500 mr-1 inline h-4 w-4" aria-hidden />
              Tu offres cette trousse. La première personne qui ouvre le lien la reçoit avec son
              numéro.
            </p>
            <div className="flex justify-center rounded-2xl bg-white p-3">
              <QrCodeImage value={trousseGiftUrl(token)} size={176} alt="QR code du cadeau" />
            </div>
            <InviteLinkField url={trousseGiftUrl(token)} shareTitle={`La trousse « ${t.name} »`} />
            <Button
              fullWidth
              variant="outline"
              loading={cancelGift.isPending}
              onClick={() => void takeBack()}
            >
              Reprendre ma trousse
            </Button>
          </div>
        ) : t.owned ? (
          <div className="space-y-2">
            <Button fullWidth size="lg" disabled={t.equipped} loading={equipping} onClick={onEquip}>
              {t.equipped ? 'Tu la portes' : 'Porter cette trousse'}
            </Button>
            {option.kind === 'ready' &&
              (confirmTrade ? (
                <div className="border-ink-100 space-y-2 rounded-2xl border p-3">
                  <p className="text-ink-700 text-sm">
                    Tu rends {tradeCost} exemplaires de « {t.name} » et tu reçois 1 exemplaire de «{' '}
                    {option.target.name} » ({trousseRarity(option.target.copies)}). Les numéros
                    rendus redeviennent libres : d’autres pourront les gagner.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button fullWidth loading={trade.isPending} onClick={() => void doTrade()}>
                      Échanger
                    </Button>
                    <Button fullWidth variant="ghost" onClick={() => setConfirmTrade(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <Button fullWidth variant="outline" onClick={() => setConfirmTrade(true)}>
                  <ArrowUpRight className="mr-2 h-4 w-4" aria-hidden />
                  Échanger {tradeCost} contre 1 « {option.target.name} »
                </Button>
              ))}
            {option.kind === 'missing' && (
              <p className="text-ink-500 text-xs">
                Encore {option.missing} exemplaire{option.missing > 1 ? 's' : ''} pour échanger{' '}
                {tradeCost} « {t.name} » contre 1 « {option.target.name} ».
              </p>
            )}
            {option.kind === 'sold_out' && t.count >= tradeCost && (
              <p className="text-ink-500 text-xs">
                Plus aucun exemplaire libre de « {option.target.name} » : l’échange n’est plus
                possible.
              </p>
            )}
            {!t.is_default &&
              (confirmGift ? (
                <div className="border-ink-100 space-y-2 rounded-2xl border p-3">
                  <p className="text-ink-700 text-sm">
                    Elle quittera ta collection dès maintenant. Tu pourras la reprendre tant que
                    personne n’a ouvert le lien.
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button fullWidth loading={createGift.isPending} onClick={() => void gift()}>
                      Créer le lien du cadeau
                    </Button>
                    <Button fullWidth variant="ghost" onClick={() => setConfirmGift(false)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <Button fullWidth variant="outline" onClick={() => setConfirmGift(true)}>
                  <Gift className="mr-2 h-4 w-4" aria-hidden />
                  Offrir cette trousse
                </Button>
              ))}
          </div>
        ) : (
          <p className="text-ink-600 flex gap-2 text-sm">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#d99a12]" aria-hidden />
            Gagne-la avec la roue du jour, ou reçois-la en cadeau d’un ami.
          </p>
        )}

        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onClose}
          className="text-ink-500 min-h-11 w-full text-sm font-semibold"
        >
          Fermer
        </button>
      </Card>
    </div>
  );
}

function TrousseFacts({ trousse: t, token }: { trousse: CollectionTrousse; token: string | null }) {
  return (
    <>
      {t.count > 1 ? (
        <p className="text-ink-500 text-xs">
          {t.count} exemplaires : {serialsLabel(t.serials)}
        </p>
      ) : t.serial != null && (t.owned || token) ? (
        <p className="text-ink-500 text-xs">{serialLabel(t.serial, t.copies)}</p>
      ) : (
        <p className="text-ink-500 text-xs">
          {t.copies <= 1
            ? 'Un seul exemplaire au monde'
            : `${formatCopies(t.copies)} exemplaires, ${formatCopies(t.taken)} déjà pris`}
        </p>
      )}
      {t.owned && t.source && <p className="text-ink-500 text-xs">{SOURCE_LABEL[t.source]}</p>}
    </>
  );
}
